import React, { useCallback, useEffect, useMemo, useState } from "react";
import PlaygroundCanvas from "../PlaygroundCanvas";
import DominosStageShell from "./components/DominosStageShell";
import DominosOverlayCard from "./components/DominosOverlayCard";
import DominosContinueButton from "./components/DominosContinueButton";
import DominosStoryboardScene from "./DominosStoryboardScene";
import { DOMINOS_SLIDES } from "./slides";

const INITIAL_UI_STATE = {
    currentSlideIndex: 0,
    currentSlideId: DOMINOS_SLIDES[0]?.id || "intro",
    isTransitioning: false,
};

function buildOverlaySlots(slide, sceneApi, currentSlideIndex, slideCount) {
    const canGoBack = currentSlideIndex > 0;
    const canContinue = currentSlideIndex < slideCount - 1;

    return {
        "top-left": (
            <DominosOverlayCard
                eyebrow={slide.eyebrow}
                title={slide.title}
                body={slide.body}
            />
        ),
        "top-right": slide.topRightBlurb ? (
            <DominosOverlayCard
                eyebrow={slide.topRightBlurb.eyebrow}
                title={slide.topRightBlurb.title}
                body={slide.topRightBlurb.body}
                align="right"
                minWidth={300}
            />
        ) : null,
        "bottom-left": (
            <DominosOverlayCard
                eyebrow="Progress"
                title={`${currentSlideIndex + 1} / ${slideCount}`}
                body={slide.footerNote}
                minWidth={280}
            />
        ),
        "bottom-right": (
            <div
                style={{
                    display: "grid",
                    gap: "12px",
                    justifyItems: "end",
                }}
            >
                {slide.controlHint ? (
                    <DominosOverlayCard
                        eyebrow="Interaction"
                        title={slide.controlHint.title}
                        body={slide.controlHint.body}
                        align="right"
                        minWidth={280}
                    />
                ) : null}

                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                    }}
                >
                    <DominosContinueButton
                        label="Back"
                        onClick={
                            canGoBack && sceneApi
                                ? () => sceneApi.goToSlide(currentSlideIndex - 1)
                                : undefined
                        }
                        disabled={!sceneApi || !canGoBack}
                        kind="secondary"
                    />

                    <DominosContinueButton
                        label={canContinue ? "Continue" : "Enter Game"}
                        onClick={
                            sceneApi
                                ? () => sceneApi.goToSlide(currentSlideIndex + 1)
                                : undefined
                        }
                        disabled={!sceneApi}
                    />
                </div>
            </div>
        ),
    };
}

export default function DominosPage() {
    const [sceneApi, setSceneApi] = useState(null);
    const [uiState, setUiState] = useState(INITIAL_UI_STATE);

    const handleSceneReady = useCallback((scene) => {
        setSceneApi(scene);
    }, []);

    useEffect(() => {
        if (!sceneApi) {
            setUiState(INITIAL_UI_STATE);
            return undefined;
        }

        if (typeof sceneApi.getUiState === "function") {
            setUiState({
                ...INITIAL_UI_STATE,
                ...sceneApi.getUiState(),
            });
        }

        if (typeof sceneApi.subscribeUiState !== "function") {
            return undefined;
        }

        return sceneApi.subscribeUiState((nextUiState) => {
            setUiState((prevState) => ({
                ...prevState,
                ...nextUiState,
            }));
        });
    }, [sceneApi]);

    const currentSlideIndex = Math.max(
        0,
        Math.min(uiState.currentSlideIndex || 0, DOMINOS_SLIDES.length - 1)
    );

    const activeSlide = DOMINOS_SLIDES[currentSlideIndex];

    const overlaySlots = useMemo(() => {
        return buildOverlaySlots(
            activeSlide,
            sceneApi,
            currentSlideIndex,
            DOMINOS_SLIDES.length
        );
    }, [activeSlide, sceneApi, currentSlideIndex]);

    const sceneProps = useMemo(() => {
        return {
            slides: DOMINOS_SLIDES,
        };
    }, []);

    return (
        <DominosStageShell overlaySlots={overlaySlots}>
            <PlaygroundCanvas
                sceneClass={DominosStoryboardScene}
                sceneProps={sceneProps}
                onSceneReady={handleSceneReady}
                style={{
                    borderRadius: "24px",
                }}
            />
        </DominosStageShell>
    );
}